import React, { useEffect, useRef, useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX } = FiIcons;

const MobileBottomSheet = ({ isOpen, onClose, children }) => {
  const sheetRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [currentY, setCurrentY] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimatingIn, setIsAnimatingIn] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsAnimatingIn(true);
        });
      });
    } else {
      setIsAnimatingIn(false);
    }
  }, [isOpen]);

  const handleTransitionEnd = () => {
    if (!isOpen && !isAnimatingIn) {
      setIsVisible(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handlePointerDown = (e) => {
    if (e.target.closest('.drag-handle')) {
      setIsDragging(true);
      setStartY(e.clientY);
      setCurrentY(0);
      sheetRef.current?.setPointerCapture(e.pointerId);
    }
  };

  const handlePointerMove = (e) => {
    if (isDragging) {
      const deltaY = e.clientY - startY;
      setCurrentY(Math.max(0, deltaY));
    }
  };

  const handlePointerUp = (e) => {
    if (isDragging) {
      const threshold = 100;
      if (currentY > threshold) {
        onClose();
      } else {
        setCurrentY(0);
      }
      setIsDragging(false);
      sheetRef.current?.releasePointerCapture(e.pointerId);
    }
  };

  const getTransform = () => {
    if (isDragging) {
      return `translateY(${currentY}px)`;
    }
    return isAnimatingIn ? 'translateY(0)' : 'translateY(100%)';
  };

  if (!isVisible) return null;

  return (
    <>
      <div
        className={`fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
          isAnimatingIn ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        ref={sheetRef}
        className="fixed bottom-0 left-0 right-0 z-[70] h-[100vh] bg-white border-t-2 border-black shadow-neo transform transition-transform duration-300 ease-out"
        style={{
          transform: getTransform(),
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onTransitionEnd={handleTransitionEnd}
      >
        <div className="drag-handle w-full flex items-center justify-between p-4 border-b-2 border-black cursor-grab active:cursor-grabbing">
          <div className="w-12 h-1.5 bg-gray-300 rounded-full" />
          <button onClick={onClose} className="p-2 hover:bg-gray-100 border-2 border-black hover:shadow-neo transition-all">
            <SafeIcon icon={FiX} size={20} />
          </button>
        </div>

        <div className="p-4 overflow-y-auto h-[calc(100vh-72px)]">
          {children}
        </div>
      </div>
    </>
  );
};

export default MobileBottomSheet;
